import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import Api from "js/core/api";
import { trackActivity } from "js/core/utilities/utilities";
import { ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { PERMISSION_RESOURCE_TYPE } from "common/constants";
import { FeatureType } from "common/features";
import log from "js/core/logger";
import { PresentationLibraryController } from "js/editor/PresentationLibrary/PresentationLibraryController";
import { Team } from "js/core/models/team";
import { _ } from "js/vendor";
import { UIController } from "js/editor/dialogs/UIController";

const folderCache = {};

export class TeamFoldersDataService {
    constructor(config) {
        this.config = config;
        ds.teams.on("add update change", this.onChange);
        this.onChange();
    }

    onChange = async model => {
        const orgId = UIController.getOrganizationId();
        const canManageTeam = app.user.features.isFeatureEnabled(FeatureType.MANAGE_TEAM, orgId);
        let teamFolders;
        if (canManageTeam) {
            const orgResponse = await Api.organizations.get({ id: app.appState.orgId });
            const teams = Object.keys(orgResponse.teams || {});
            teams.push(orgResponse.defaultTeamId);
            teamFolders = await Promise.all(teams.map(async teamId => {
                let cachedFolder = folderCache[teamId];

                if (model?.id === teamId) {
                    // delete from cache and re-fetch below
                    delete folderCache[teamId];
                } else if (cachedFolder) {
                    // if the folder wasn't updated, cached version should still be good
                    return cachedFolder;
                }

                let folder = await Api.teams.get({ id: teamId }).then(response => response.body);
                folder.presentationCount = (folder.sharedResources && folder.sharedResources[0]) ? Object.keys(folder.sharedResources[0]).length : 0;
                folder.memberCount = folder.members.length;
                folderCache[teamId] = folder;
                return folder;
            }));
        } else {
            teamFolders = ds.teams.filter(folder => folder.get("orgId") === orgId).map(folder => folder.attributes).map(folder => {
                return {
                    ...folder,
                    presentationCount: (folder.sharedResources && folder.sharedResources[0]) ? Object.keys(folder.sharedResources[0]).length : 0,
                    memberCount: Object.keys(folder.members || {}).length
                };
            });
        }
        this.config.onCollectionChanged(teamFolders);
    }

    unsubscribe() {
        ds.teams.off("add update change", this.onChange);
    }
}

export class TeamFoldersController {
    static getAllTeamMembersFolder = () => {
        return ds.teams.find(teamFolder => teamFolder.get("isDefault") && teamFolder.get("orgId") == UIController.getOrganizationId());
    }

    static createTeamFolder = async (name, addCurrentUser) => {
        const { body: folder } = await Api.teams.post({ teamName: name, orgId: app.appState.orgId });

        const props = {
            workspace_id: app.appState.orgId,
            team_id: folder.id,
            team_name: name
        };
        trackActivity("OrgTeam", "Created", null, null, props);

        if (addCurrentUser) {
            await Api.teams.put({
                teamId: folder.id,
                orgId: app.appState.orgId,
                type: "add_existing_members",
                userIds: [app.user.id]
            });
        }

        let folderModel = new Team({ id: folder.id });
        await folderModel.load();

        return folderModel.toJSON();
    }

    static getTeamMembersForFolder = async folderId => {
        const response = await Api.teams.get({ id: folderId });
        const teamInvitesResponse = await Api.teamInvites.get({
            type: "team",
            id: folderId
        });

        let allMembers = response.body.members.concat(teamInvitesResponse.body);
        allMembers = _.sortBy(allMembers, "email");

        return allMembers;
    }

    static deleteTeamFolder = async folder => {
        await Api.teams.delete({
            teamId: folder.id,
            orgId: app.appState.orgId
        });

        const props = {
            workspace_id: app.appState.orgId,
            team_id: folder.id,
            team_name: folder.name
        };
        trackActivity("OrgFolder", "Deleted", null, null, props);
    }

    static renameTeamFolder = async (folder, name) => {
        if (folder.isDefault) {
            throw new Error("Can't edit default team name");
        }

        // optimally update our in memory model if we have one, otherwise use the API
        const teamFolder = ds.teams.get(folder.id);
        if (teamFolder) {
            teamFolder.update({ name });
        } else {
            await Api.teams.put({ type: "change_team_name", teamId: folder.id, teamName: name });
        }
    }

    static setOnlyOwnerCanEdit = async (folder, value) => {
        const teamFolder = ds.teams.get(folder.id);
        if (teamFolder) {
            teamFolder.update({ onlyOwnerCanEdit: value });
        } else {
            // await Api.teams.put({ type: "change_team_name", teamId: folder.id, teamName: name });
        }
    }

    static setTemplatePermissions = async (templates, teamFolderId) => {
        const presentationIds = templates.map(selectedPresentation => selectedPresentation.id);

        const progressDialog = ShowDialog(ProgressDialog, {
            title: `Creating template...`,
        });
        try {
            await Api.teamPermissions.put({
                teamId: teamFolderId,
                resourceIds: presentationIds,
                resourceType: "presentations",
                permission: "write",
                type: "create-template"
            });
        } catch (err) {
            log.error(err);
        }
        progressDialog.props.closeDialog();
    }

    static deleteTemplatePermissions = async (templates, teamFolderId) => {
        const presentationIds = templates.map(selectedPresentation => selectedPresentation.id);

        const progressDialog = ShowDialog(ProgressDialog, {
            title: `Deleting template...`,
        });
        try {
            await Api.teamPermissions.put({
                teamId: teamFolderId,
                resourceIds: presentationIds,
                resourceType: "presentations",
                permission: "write",
                type: "delete-template"
            });
        } catch (err) {
            log.error(err);
        }
        progressDialog.props.closeDialog();
    }

    static addPresentationsToTeamFolder = async (presentations, teamFolderId, permission) => {
        //Recover any presentations that are in the trash before sharing them with a team.
        PresentationLibraryController.recoverPresentations(presentations);

        let team = ds.teams.get(teamFolderId);

        await Promise.all(presentations.map(presentation => presentation.getUserPermissions(true)));
        const presentationIds = presentations.map(selectedPresentation => selectedPresentation.id);
        const viewOnlyPresentationList = presentations.filter(presentation => !presentation.permissions.owner && !presentation.permissions.write && !presentation.get("softDeletedAt"));

        if (viewOnlyPresentationList.length) {
            ShowErrorDialog({
                error: `Unable to share ${"presentation".pluralize(presentations.length > 1)} with ${team.get("name")}.`,
                message: "View only presentations cannot be shared."
            });
        } else {
            const progressDialog = ShowDialog(ProgressDialog, {
                title: `Sharing presentations with ${team.get("name")}...`,
            });
            try {
                await Api.teamPermissions.put({
                    teamId: teamFolderId,
                    resourceIds: presentationIds,
                    permission,
                    resourceType: "presentations",
                    type: "access"
                });
                const permissionObj = (permission === "write") ? { write: true, read: true } : { read: true };
                presentationIds.forEach(id => {
                    if (!team.has("sharedResources")) {
                        team.update({ "sharedResources": [{ [id]: permissionObj }] });
                    } else {
                        team.get("sharedResources")[PERMISSION_RESOURCE_TYPE.PRESENTATION][id] = permissionObj;
                    }
                });
            } catch (err) {
                log.error(err);
            }
            progressDialog.props.closeDialog();
        }
    }

    static removePresentationFromTeamFolder = async (presentation, teamFolderId) => {
        try {
            await Api.teamPermissions.delete({
                teamId: teamFolderId,
                resourceIds: [presentation.id],
                resourceType: "presentations"
            });

            // this is a hack so the presentation library updates because we don't have a team folder dataservice
            ds.presentations.trigger("change", ds.presentations);
        } catch (err) {
            log.error(err);
        }
    }

    static addMembersToTeamFolder = async (folder, userIds) => {
        await Api.teams.put({
            teamId: folder.id,
            orgId: app.appState.orgId,
            type: "add_existing_members",
            userIds
        });

        const props = {
            workspace_id: app.appState.orgId,
            team_id: folder.id,
            team_name: folder.name,
            userid_added: userIds,
            userid_removed: null,
            num_team_members: (folder.members ? folder.members.length : 0) + userIds.length,
            num_changed: userIds.length,
            change_mechanism: "admin initiated"
        };
        trackActivity("OrgTeam", "MembershipChanged", null, null, props);
    }

    static removeMembersFromTeamFolder = async (folder, userIds, isRemovingSelf) => {
        const teamFolder = ds.teams.get(folder.id);
        if (teamFolder && isRemovingSelf) {
            teamFolder.disconnect();
        }
        await Api.teams.put({
            orgId: app.appState.orgId,
            teamId: folder.id,
            type: "remove_users",
            userIds: userIds
        });

        const props = {
            workspace_id: app.appState.orgId,
            team_id: folder.id,
            team_name: folder.name,
            userid_added: null,
            userid_removed: userIds,
            num_team_members: folder.members.length - 1,
            num_changed: -1,
            change_mechanism: isRemovingSelf ? "user initiated" : "admin initiated"
        };
        trackActivity("OrgTeam", "MembershipChanged", null, null, props, { audit: true });
    }
}
